/* Local procurement quote only. No orders, inventory reservations, or payment actions.
 * Contract: external card controls are read through form.elements.
 * Persistence: schema 1, updatedAt (epoch milliseconds), 30-day expiry, 200 lines.
 * Prices are reference snapshots in Shopify's integer money representation (scale 100).
 * Code/notes/intent are never persisted. The browser owns no customer/contact record.
 */
(function procurementQuote(global) {
  'use strict';

  if (global.document && global.TerplaxProcurementQuote) {
    global.TerplaxProcurementQuote.initialize();
    return;
  }

  const TTL = 30 * 24 * 60 * 60 * 1000;
  const MAX_QUANTITY = 9999;
  const MAX_LINES = 200;
  const MAX_MESSAGE = 6500;
  const MAX_STORAGE_BYTES = 2000000;
  const defaults = Object.freeze({
    added: 'Added to procurement list.', storage_error: 'Storage unavailable. This list is kept only in this page; refreshing may lose it.',
    invalid_quantity: 'Enter a whole-number quantity from 1 to 9999 and select a valid specification.',
    empty: 'Your procurement list is empty.', expired: 'Your saved list expired after 30 days.',
    subtotal: 'Reference subtotal', reference_note: 'Reference only. Final price, stock, shipping and taxes require confirmation. This is not an order or payment.',
    remove: 'Remove', size: 'Specification', quantity: 'Quantity', copy: 'Copy full list', copied: 'Full list copied.',
    download: 'Download full list', quote: 'Request a quotation', question: 'Ask about these products',
    discount_note: 'Request a discount review; no discount is automatically applied.', code_label: 'Promotional code',
    code_note: 'Code for customer-service review only; not validated or applied.', notes_label: 'Notes',
    send_notice: 'Opens WhatsApp with the list prefilled. Review it and send it yourself. It has not been sent.',
    long_notice: 'The full list is too long for the chat link. Copy or download the complete list instead.',
    unit_price: 'Reference unit price', line_total: 'Reference line total',
    currency_note: 'Do not combine currencies. Remove the existing list before adding items in another currency.',
    max_lines: 'The procurement list can contain at most 200 lines.',
    copy_error: 'Automatic copy failed. Select and copy the complete text below, or download it.',
    title: 'Procurement list', number: 'Catalog number', discount: 'Discuss discounts',
    total_notice: 'No order, payment or stock reservation is created.'
  });

  /** Strict parsing deliberately rejects exponent/decimal/partially numeric input. */
  function quantity(value) {
    const text = String(value == null ? '' : value).trim();
    if (!/^\d{1,4}$/.test(text)) return null;
    const number = Number(text);
    return number >= 1 && number <= MAX_QUANTITY ? number : null;
  }

  function minor(value) {
    if (!/^\d+$/.test(String(value))) return null;
    const number = Number(value);
    return Number.isSafeInteger(number) && number >= 0 ? number : null;
  }

  function currency(value) {
    const code = String(value || '').toUpperCase();
    return /^[A-Z]{3}$/.test(code) ? code : null;
  }

  function text(value, limit) {
    if (typeof value !== 'string' || value.length > limit) throw new Error('invalid_record');
    return value.replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, '');
  }

  function productURL(value, origin) {
    if (!value) return '';
    try {
      const url = new URL(value, origin);
      if (url.origin !== new URL(origin).origin || !/^https?:$/.test(url.protocol)) return '';
      // Only the canonical product path goes into persistence, never query/customer data.
      return /\/products\/[^/]+\/?$/.test(url.pathname) ? url.pathname : '';
    } catch (_) { return ''; }
  }

  /** Whitelist fields: never serialize caller-supplied notes, codes or contact data. */
  function cleanItem(item, origin) {
    if (!item || typeof item !== 'object') throw new Error('invalid_record');
    const q = quantity(item.quantity);
    const price = minor(item.price);
    const code = currency(item.currency);
    if (!/^\d{1,30}$/.test(String(item.variantId)) || !/^\d{1,30}$/.test(String(item.productId)) ||
        q === null || price === null || !code || !Number.isSafeInteger(q * price)) throw new Error('invalid_record');
    const snapshot = Number(item.priceSnapshotAt);
    return {
      productId: String(item.productId), variantId: String(item.variantId),
      title: text(item.title, 500), code: text(item.code || '', 40), series: text(item.series || '', 160),
      url: productURL(item.url || '', origin), sku: text(item.sku || '', 160), size: text(item.size || '', 200),
      currency: code, quantity: q, price, priceLabel: text(item.priceLabel || '', 300),
      priceSnapshotAt: Number.isSafeInteger(snapshot) && snapshot >= 0 ? snapshot : 0
    };
  }

  function subtotal(items) {
    let total = 0;
    let code = null;
    for (const item of items) {
      if (code && code !== item.currency) throw new Error('currency_note');
      code = item.currency;
      const line = item.price * item.quantity;
      if (!Number.isSafeInteger(line) || !Number.isSafeInteger(total + line)) throw new Error('invalid_quantity');
      total += line;
    }
    return total;
  }

  function mergeItems(items, candidate) {
    if (items.some((item) => item.currency !== candidate.currency)) throw new Error('currency_note');
    const result = items.map((item) => ({ ...item }));
    const index = result.findIndex((item) => item.variantId === candidate.variantId);
    if (index === -1) {
      if (result.length >= MAX_LINES) throw new Error('max_lines');
      result.push({ ...candidate });
    } else {
      const previous = result[index];
      if (previous.productId !== candidate.productId || previous.sku !== candidate.sku) throw new Error('invalid_quantity');
      const sum = previous.quantity + candidate.quantity;
      if (quantity(sum) === null) throw new Error('invalid_quantity');
      result[index] = { ...candidate, quantity: sum };
    }
    subtotal(result);
    return result;
  }

  function parseRecord(raw, now, origin) {
    if (raw === null || raw === '') return { items: [], updatedAt: now, notice: '' };
    try {
      if (typeof raw !== 'string' || raw.length > MAX_STORAGE_BYTES) throw new Error('invalid_record');
      const record = JSON.parse(raw);
      if (record.schema !== 1 || !Number.isSafeInteger(record.updatedAt) || record.updatedAt < 0 ||
          record.updatedAt > now + 60000 || !Array.isArray(record.items) || record.items.length > MAX_LINES) throw new Error('invalid_record');
      if (now - record.updatedAt >= TTL) return { items: [], updatedAt: now, notice: 'expired' };
      const items = [];
      for (const rawItem of record.items) items.push(cleanItem(rawItem, origin));
      const ids = new Set(items.map((item) => item.variantId));
      if (ids.size !== items.length) throw new Error('invalid_record');
      subtotal(items);
      return { items, updatedAt: record.updatedAt, notice: '' };
    } catch (_) {
      return { items: [], updatedAt: now, notice: 'storage_error' };
    }
  }

  function serializeRecord(items, now, origin) {
    if (items.length > MAX_LINES || new Set(items.map((item) => item.variantId)).size !== items.length) throw new Error('invalid_record');
    const clean = items.map((item) => cleanItem(item, origin));
    subtotal(clean);
    return JSON.stringify({ schema: 1, updatedAt: now, items: clean });
  }

  function money(amount, code, locale) {
    try {
      return new Intl.NumberFormat(locale || 'en', { style: 'currency', currency: code, currencyDisplay: 'code' }).format(amount / 100);
    } catch (_) { return `${code} ${(amount / 100).toFixed(2)}`; }
  }

  function quoteText(items, labels, settings) {
    const l = { ...defaults, ...labels };
    if (!items.length) return l.empty;
    const options = settings || {};
    const code = items[0].currency;
    const intent = ['quote', 'question', 'discount'].includes(options.intent) ? options.intent : 'quote';
    const lines = [l.title, intent === 'discount' ? l.discount_note : l[intent], ''];
    for (let index = 0; index < items.length; index++) {
      const item = items[index];
      lines.push(`${index + 1}. ${item.title}`,
        `${l.number}: ${item.code || '—'} | ${item.series}`,
        `SKU: ${item.sku || '—'} | Variant: ${item.variantId}`,
        `${l.size}: ${item.size} | ${l.quantity}: ${item.quantity}`,
        `${l.unit_price}: ${item.priceLabel || money(item.price, item.currency, options.locale)}`,
        `${l.line_total}: ${money(item.price * item.quantity, item.currency, options.locale)}`,
        item.url && options.origin ? new URL(item.url, options.origin).href : '',
        '');
    }
    lines.push(`${l.subtotal}: ${money(subtotal(items), code, options.locale)} (${code})`);
    if (options.code) lines.push(`${l.code_label}: ${options.code}`, l.code_note);
    // All intents make the non-applied-discount status explicit, not just discount inquiries.
    if (intent !== 'discount') lines.push(l.discount_note);
    if (options.notes) lines.push(`${l.notes_label}: ${options.notes}`);
    lines.push('', l.reference_note, l.total_notice, l.send_notice);
    return lines.join('\n');
  }

  function contactLink(phone, message, populated) {
    if (!populated) return { href: '', reason: 'empty' };
    if (message.length > MAX_MESSAGE) return { href: '', reason: 'long_notice' };
    let digits = String(phone || '').trim();
    if (/^https:\/\/wa\.me\//.test(digits)) {
      try {
        const url = new URL(digits);
        if (url.hostname !== 'wa.me' || url.search || url.hash) return { href: '', reason: 'send_notice' };
        digits = url.pathname.slice(1);
      } catch (_) { return { href: '', reason: 'send_notice' }; }
    }
    digits = digits.replace(/[+\s()-]/g, '');
    if (!/^[1-9]\d{6,14}$/.test(digits)) return { href: '', reason: 'send_notice' };
    const url = new URL(`https://wa.me/${digits}`);
    url.searchParams.set('text', message);
    if (url.href.length > MAX_MESSAGE) return { href: '', reason: 'long_notice' };
    return { href: url.href, reason: '' };
  }

  /** Select only an actual, enabled option in the form's associated select. */
  function readCard(form, code, origin, now) {
    const picker = form.elements.namedItem('id');
    const count = form.elements.namedItem('quantity');
    if (!picker || picker.tagName !== 'SELECT' || picker.disabled || !count || count.disabled) throw new Error('invalid_quantity');
    const option = picker.options[picker.selectedIndex];
    if (!option || option.disabled || (option.parentElement && option.parentElement.tagName === 'OPTGROUP' && option.parentElement.disabled) ||
        option.value !== picker.value || !code) throw new Error('invalid_quantity');
    return cleanItem({
      productId: form.dataset.productId, variantId: option.value,
      title: form.dataset.title, code: form.dataset.code, series: form.dataset.series, url: form.dataset.url,
      sku: option.dataset.sku, size: option.dataset.size, price: option.dataset.price,
      priceLabel: option.dataset.priceLabel, quantity: count.value, currency: code, priceSnapshotAt: now
    }, origin);
  }

  const pure = Object.freeze({ TTL, MAX_QUANTITY, MAX_LINES, MAX_MESSAGE, defaults, quantity, minor, currency,
    cleanItem, subtotal, mergeItems, parseRecord, serializeRecord, money, quoteText, contactLink, readCard });
  const document = global.document;
  const stores = new Map();
  const controllers = new Map();
  let listenersInstalled = false;
  let rowSequence = 0;

  function labelsFor(root) {
    const result = { ...defaults };
    const script = root.querySelector('[data-procurement-labels]') || document.querySelector('[data-procurement-labels]');
    try {
      const parsed = JSON.parse(script ? script.textContent : '{}');
      const supplied = parsed.labels || parsed;
      for (const key of Object.keys(defaults)) {
        if (typeof supplied[key] === 'string' && supplied[key]) result[key] = supplied[key];
      }
    } catch (_) { /* Missing/invalid translation data does not break a local list. */ }
    return result;
  }

  function origin() { return global.location.origin; }

  function getStore(key) {
    if (stores.has(key)) return stores.get(key);
    const store = { key, items: [], updatedAt: Date.now(), notice: '', storageFailed: false };
    stores.set(key, store);
    reload(store);
    return store;
  }

  function reload(store, raw) {
    if (store.storageFailed) return; // Never destroy the explicitly retained memory fallback.
    try {
      const result = parseRecord(raw === undefined ? global.localStorage.getItem(store.key) : raw, Date.now(), origin());
      store.items = result.items;
      store.updatedAt = result.updatedAt;
      store.notice = result.notice;
    } catch (_) {
      store.storageFailed = true;
      store.notice = 'storage_error';
    }
  }

  function persist(store) {
    store.updatedAt = Date.now();
    if (store.storageFailed) return;
    try {
      global.localStorage.setItem(store.key, serializeRecord(store.items, store.updatedAt, origin()));
      store.notice = '';
    } catch (_) {
      store.storageFailed = true;
      store.notice = 'storage_error';
    }
  }

  function renderStore(store, message, preserveController) {
    for (const controller of controllers.values()) {
      if (controller.root.isConnected && controller.store === store) {
        if (controller === preserveController) controller.refreshAmounts(message);
        else controller.render(message);
      }
    }
    updateBubbles();
  }

  function mutate(controller, change, message, preserveInput) {
    const store = controller.store;
    reload(store); // Merge against the latest other-tab write before a local mutation.
    try {
      const next = change(store.items.map((item) => ({ ...item })));
      subtotal(next);
      store.items = next;
      persist(store);
      controller.lastError = '';
      renderStore(store, message, preserveInput ? controller : null);
      return true;
    } catch (error) {
      controller.lastError = error.message in defaults ? error.message : 'invalid_quantity';
      renderStore(store, controller.lastError, preserveInput ? controller : null);
      return false;
    }
  }

  function element(tag, className, value) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (value !== undefined) node.textContent = value;
    return node;
  }

  function activeController() {
    const available = Array.from(controllers.values()).filter((controller) => controller.root.isConnected);
    return available.find((controller) => controller.root.tagName !== 'DIALOG') || available[0];
  }

  class QuoteController {
    constructor(root) {
      this.root = root;
      this.labels = labelsFor(root);
      this.currency = currency(root.dataset.currency);
      this.locale = root.dataset.locale || 'en';
      this.store = getStore(root.dataset.storageKey);
      this.returnFocus = null;
      this.message = '';
      this.q = (suffix) => root.querySelector(`[data-procurement-${suffix}]`);
      const status = this.q('status');
      if (status) { status.setAttribute('aria-live', 'polite'); status.setAttribute('aria-atomic', 'true'); }
      root.addEventListener('click', (event) => this.click(event));
      root.addEventListener('input', (event) => this.input(event));
      root.addEventListener('change', (event) => this.change(event));
      root.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.target.matches('[data-procurement-line-quantity]')) event.preventDefault();
      });
      root.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); });
      root.addEventListener('close', () => this.restoreFocus());
    }

    settings() {
      return { locale: this.locale, origin: origin(), intent: this.q('intent')?.value || 'quote',
        code: this.q('code')?.value || '', notes: this.q('notes')?.value || '' };
    }

    announce(message) {
      const status = this.q('status');
      const parts = [];
      if (message && this.labels[message]) parts.push(this.labels[message]);
      if (this.store.notice) parts.push(this.labels[this.store.notice]);
      const mismatch = this.store.items.length && this.store.items[0].currency !== this.currency;
      if (mismatch) parts.push(this.labels.currency_note);
      if (this.linkReason) parts.push(this.labels[this.linkReason]);
      if (status) status.textContent = Array.from(new Set(parts)).join(' ');
    }

    sync(message) {
      this.message = quoteText(this.store.items, this.labels, this.settings());
      const link = contactLink(this.root.dataset.whatsapp, this.message, this.store.items.length > 0);
      const invalid = this.root.querySelector('[data-procurement-line-quantity][aria-invalid="true"]');
      this.linkReason = invalid ? 'invalid_quantity' : link.reason;
      const contact = this.q('contact');
      if (contact) {
        contact.target = '_blank';
        contact.rel = 'noopener noreferrer';
        contact.textContent = this.labels[this.settings().intent] || this.labels.quote;
        if (link.href && !invalid) { contact.href = link.href; contact.removeAttribute('aria-disabled'); }
        else { contact.removeAttribute('href'); contact.setAttribute('aria-disabled', 'true'); }
      }
      const sendNotice = this.q('send-notice');
      if (sendNotice) { sendNotice.textContent = this.labels.send_notice; sendNotice.hidden = !link.href || !!invalid; }
      const longNotice = this.q('long-notice');
      if (longNotice) { longNotice.textContent = this.labels.long_notice; longNotice.hidden = link.reason !== 'long_notice'; }
      const manual = this.q('manual-chat');
      if (manual) manual.hidden = link.reason !== 'long_notice';
      const preview = this.q('text');
      if (preview) preview.value = this.message;
      this.announce(message);
    }

    refreshAmounts(message) {
      const items = this.store.items;
      const total = this.q('total');
      if (total) total.textContent = money(subtotal(items), items[0]?.currency || this.currency || 'USD', this.locale);
      const list = this.q('items');
      if (list) {
        for (const row of list.children) {
          const item = items.find((entry) => entry.variantId === row.dataset.variantId);
          if (!item) { this.render(message); return; }
          const amount = row.querySelector('[data-procurement-line-total]');
          if (amount) amount.textContent = `${this.labels.line_total}: ${money(item.price * item.quantity, item.currency, this.locale)}`;
          for (const button of row.querySelectorAll('[data-procurement-step]')) {
            button.disabled = Number(button.dataset.procurementStep) < 0 ? item.quantity <= 1 : item.quantity >= MAX_QUANTITY;
          }
        }
      }
      this.sync(message);
    }

    render(message) {
      const items = this.store.items;
      const empty = this.q('empty');
      const filled = this.q('filled');
      if (empty) {
        empty.hidden = items.length > 0;
        const heading = empty.querySelector('h3');
        if (heading) heading.textContent = this.labels.empty;
        else if (!empty.children.length) empty.textContent = this.labels.empty;
      }
      if (filled) filled.hidden = items.length === 0;
      const total = this.q('total');
      if (total) total.textContent = money(subtotal(items), items[0]?.currency || this.currency || 'USD', this.locale);
      for (const suffix of ['copy', 'download']) {
        const button = this.q(suffix);
        if (button) { button.disabled = !items.length; button.textContent = this.labels[suffix]; }
      }
      const list = this.q('items');
      if (list) {
        const fragment = document.createDocumentFragment();
        for (const item of items) fragment.append(this.row(item));
        list.replaceChildren(fragment);
      }
      this.sync(message);
    }

    row(item) {
      const l = this.labels;
      const listTag = this.q('items')?.tagName;
      const row = element(['UL', 'OL'].includes(listTag) ? 'li' : 'article', 'tplx-procurement__item');
      row.dataset.variantId = item.variantId;
      const heading = element('h3', 'tplx-procurement__item-title', item.title);
      heading.id = `ProcurementItem-${++rowSequence}`;
      row.setAttribute('aria-labelledby', heading.id);
      row.append(heading);
      row.append(element('p', 'tplx-procurement__item-meta', `${item.series} · ${l.number}: ${item.code || '—'} · SKU: ${item.sku || '—'}`));
      row.append(element('p', 'tplx-procurement__item-meta', `${l.size}: ${item.size}`));
      row.append(element('p', 'tplx-procurement__item-price', `${l.unit_price}: ${item.priceLabel || money(item.price, item.currency, this.locale)}`));
      const controls = element('div', 'tplx-procurement__item-controls');
      const id = `ProcurementQuantity-${++rowSequence}`;
      const label = element('label', 'tplx-procurement__label', l.quantity);
      label.htmlFor = id;
      controls.append(label);
      const field = element('input', 'tplx-procurement__input');
      field.type = 'number'; field.min = '1'; field.max = String(MAX_QUANTITY); field.step = '1';
      field.inputMode = 'numeric'; field.id = id; field.value = String(item.quantity);
      field.style.minHeight = '44px';
      field.setAttribute('data-procurement-line-quantity', '');
      field.setAttribute('aria-label', `${l.quantity}: ${item.title}, ${item.size}`);
      for (const [sign, delta] of [['−', -1], ['+', 1]]) {
        const button = element('button', 'tplx-procurement__remove', sign);
        button.type = 'button'; button.dataset.procurementStep = String(delta);
        button.setAttribute('aria-label', `${sign} ${l.quantity}: ${item.title}, ${item.size}`);
        button.style.minWidth = '44px'; button.style.minHeight = '44px';
        button.disabled = delta < 0 ? item.quantity <= 1 : item.quantity >= MAX_QUANTITY;
        if (delta < 0) controls.append(button, field);
        else controls.append(button);
      }
      row.append(controls);
      const lineTotal = element('p', 'tplx-procurement__item-price', `${l.line_total}: ${money(item.price * item.quantity, item.currency, this.locale)}`);
      lineTotal.setAttribute('data-procurement-line-total', '');
      row.append(lineTotal);
      const remove = element('button', 'tplx-procurement__remove', l.remove);
      remove.type = 'button'; remove.setAttribute('data-procurement-remove', '');
      remove.setAttribute('aria-label', `${l.remove}: ${item.title}, ${item.size}`);
      remove.style.minHeight = '44px';
      row.append(remove);
      return row;
    }

    focusLine(variantId, selector) {
      const list = this.q('items');
      if (!list) return;
      for (const row of list.children) {
        if (row.dataset.variantId === variantId) { row.querySelector(selector)?.focus(); return; }
      }
      this.q('close')?.focus();
    }

    edit(field, preserveInput) {
      const row = field.closest('[data-variant-id]');
      const value = quantity(field.value);
      if (!row || value === null) {
        field.setAttribute('aria-invalid', 'true');
        field.setCustomValidity(this.labels.invalid_quantity);
        this.sync('invalid_quantity');
        return;
      }
      field.removeAttribute('aria-invalid'); field.setCustomValidity('');
      const updated = mutate(this, (items) => {
        const item = items.find((entry) => entry.variantId === row.dataset.variantId);
        if (!item) throw new Error('invalid_quantity');
        item.quantity = value;
        return items;
      }, undefined, preserveInput);
      if (!preserveInput) this.focusLine(row.dataset.variantId, '[data-procurement-line-quantity]');
      if (!updated && preserveInput) {
        field.setAttribute('aria-invalid', 'true');
        this.sync('invalid_quantity');
      }
    }

    input(event) {
      if (event.target.matches('[data-procurement-code], [data-procurement-notes], [data-procurement-intent]')) this.sync();
      if (event.target.matches('[data-procurement-line-quantity]')) {
        // Update valid edits immediately, but never replace the focused input per digit.
        const valid = quantity(event.target.value) !== null;
        event.target.setCustomValidity(valid ? '' : this.labels.invalid_quantity);
        event.target.setAttribute('aria-invalid', String(!valid));
        if (valid) this.edit(event.target, true);
        else this.sync('invalid_quantity');
      }
    }

    change(event) {
      if (event.target.matches('[data-procurement-line-quantity]')) this.edit(event.target);
      else if (event.target.matches('[data-procurement-intent], [data-procurement-code], [data-procurement-notes]')) this.sync();
    }

    click(event) {
      const target = event.target.closest('button, a');
      if (!target || !this.root.contains(target)) return;
      if (target.matches('[data-procurement-close]')) { event.preventDefault(); this.close(); }
      if (target.matches('[data-procurement-copy]')) { event.preventDefault(); this.copy(); }
      if (target.matches('[data-procurement-download]')) { event.preventDefault(); this.download(); }
      if (target.matches('[data-procurement-contact]')) {
        this.sync();
        if (!target.hasAttribute('href')) event.preventDefault();
        // No programmatic navigation, send, or list clearing occurs here.
      }
      const row = target.closest('[data-variant-id]');
      if (!row) return;
      const id = row.dataset.variantId;
      if (target.matches('[data-procurement-remove]')) {
        event.preventDefault();
        const next = row.nextElementSibling?.dataset.variantId || row.previousElementSibling?.dataset.variantId;
        mutate(this, (items) => items.filter((item) => item.variantId !== id));
        this.focusLine(next, '[data-procurement-remove]');
      } else if (target.matches('[data-procurement-step]')) {
        event.preventDefault();
        const delta = Number(target.dataset.procurementStep);
        if (![-1, 1].includes(delta)) return;
        mutate(this, (items) => {
          const item = items.find((entry) => entry.variantId === id);
          if (!item || quantity(item.quantity + delta) === null) throw new Error('invalid_quantity');
          item.quantity += delta;
          return items;
        });
        this.focusLine(id, '[data-procurement-line-quantity]');
      }
    }

    open(trigger) {
      reload(this.store);
      refreshSnapshots(this.store);
      renderStore(this.store);
      if (this.root.tagName === 'DIALOG') {
        if (this.root.open) { this.close(); return; }
        this.returnFocus = trigger || document.activeElement;
        if (typeof this.root.showModal !== 'function') {
          // Unsupported dialog: navigate only to the configured local procurement page.
          try {
            if (!this.root.dataset.cartUrl) return;
            const url = new URL(this.root.dataset.cartUrl, origin());
            if (url.origin === origin() && /^https?:$/.test(url.protocol)) global.location.assign(url.href);
          } catch (_) { /* Keep the page unchanged if the local fallback is invalid. */ }
          return;
        }
        this.root.showModal();
        this.q('close')?.focus();
      } else {
        this.root.scrollIntoView({ block: 'start', behavior: 'auto' });
        if (!this.root.hasAttribute('tabindex')) this.root.tabIndex = -1;
        this.root.focus({ preventScroll: true });
      }
    }

    restoreFocus() {
      if (this.returnFocus?.isConnected) this.returnFocus.focus();
      this.returnFocus = null;
    }

    close() {
      if (this.root.tagName === 'DIALOG' && this.root.open) this.root.close();
      this.restoreFocus();
    }

    async copy() {
      if (!this.store.items.length) return;
      this.sync();
      const complete = this.message;
      try {
        if (!global.navigator.clipboard?.writeText) throw new Error('clipboard');
        await global.navigator.clipboard.writeText(complete);
        this.sync('copied');
      } catch (_) {
        let preview = this.q('text');
        if (!preview) {
          preview = element('textarea', 'tplx-procurement__input');
          preview.setAttribute('data-procurement-text', '');
          preview.setAttribute('aria-label', this.labels.copy);
          preview.readOnly = true;
          this.root.append(preview);
        }
        preview.value = complete; preview.hidden = false; preview.focus(); preview.select();
        this.sync('copy_error');
      }
    }

    download() {
      if (!this.store.items.length) return;
      this.sync();
      const blob = new global.Blob([this.message], { type: 'text/plain;charset=utf-8' });
      const url = global.URL.createObjectURL(blob);
      const anchor = element('a');
      anchor.href = url; anchor.download = 'terplax-procurement-list.txt';
      document.body.append(anchor); anchor.click(); anchor.remove();
      global.setTimeout(() => global.URL.revokeObjectURL(url), 1000);
    }
  }

  function controllerForForm(form) {
    const attached = form.closest('[data-procurement-root]');
    return (attached && controllers.get(attached)) || activeController();
  }

  function updateCard(form) {
    const controller = controllerForForm(form);
    if (!controller) return;
    const picker = form.elements.namedItem('id');
    const option = picker?.options?.[picker.selectedIndex];
    let valid = false;
    let snapshot = null;
    try { snapshot = readCard(form, controller.currency, origin(), Date.now()); valid = true; } catch (_) { /* Invalid controls stay disabled. */ }
    for (const control of Array.from(form.elements)) {
      if ((control.tagName === 'BUTTON' || control.tagName === 'INPUT') && control.type === 'submit') control.disabled = !valid;
    }
    // Explicit collection-card opt-in; unit-price fields and persistence stay unchanged.
    const totals = form.querySelectorAll('[data-procurement-card-total]');
    for (const node of totals) {
      const total = snapshot ? snapshot.price * snapshot.quantity : null;
      const safe = total !== null && Number.isSafeInteger(total);
      node.hidden = !safe;
      node.textContent = safe ? money(total, snapshot.currency, controller.locale) : '';
      if (safe) node.setAttribute('aria-label', `${controller.labels.line_total}: ${node.textContent}`);
      else node.removeAttribute('aria-label');
    }
    if (!option || option.disabled || option.value !== picker.value || minor(option.dataset.price) === null) return;
    // Displays may be inside the external form, or in the card containing an associated control.
    const nodes = new Set(form.querySelectorAll('[data-procurement-price]'));
    const card = picker.closest('[data-procurement-card], .tplx-flavor__card');
    if (card) for (const node of card.querySelectorAll('[data-procurement-price]')) nodes.add(node);
    const caption = option.dataset.priceLabel || money(Number(option.dataset.price), controller.currency || 'USD', controller.locale);
    for (const node of nodes) node.textContent = caption;
  }

  function cardStatus(form, controller, message) {
    const field = form.elements.namedItem('quantity');
    const node = form.querySelector('[data-procurement-card-status]') ||
      field?.closest('[data-procurement-card], .tplx-flavor__card')?.querySelector('[data-procurement-card-status]');
    if (!node) return;
    node.textContent = [controller.labels[message], controller.labels[controller.store.notice]].filter(Boolean).join(' ');
  }

  function refreshSnapshots(store) {
    let changed = false;
    for (const form of document.querySelectorAll('form[data-procurement-add]')) {
      const controller = controllerForForm(form);
      if (!controller || controller.store !== store) continue;
      const picker = form.elements.namedItem('id');
      if (!picker?.options) continue;
      for (const option of picker.options) {
        if (option.disabled || (option.parentElement?.tagName === 'OPTGROUP' && option.parentElement.disabled)) continue;
        const item = store.items.find((entry) => entry.variantId === option.value && entry.productId === form.dataset.productId && entry.currency === controller.currency);
        const price = minor(option.dataset.price);
        if (!item || price === null || !Number.isSafeInteger(price * item.quantity)) continue;
        const priceLabel = option.dataset.priceLabel || '';
        if (priceLabel.length > 300 || item.sku !== (option.dataset.sku || '')) continue;
        if (item.price !== price || item.priceLabel !== priceLabel) {
          const next = store.items.map((entry) => entry === item ? { ...entry, price, priceLabel, priceSnapshotAt: Date.now() } : entry);
          try { subtotal(next); } catch (_) { continue; }
          store.items = next;
          changed = true;
        }
      }
    }
    if (changed) persist(store);
  }

  function updateBubbles() {
    const controller = activeController();
    if (!controller) return;
    const count = controller.store.items.reduce((sum, item) => sum + item.quantity, 0);
    for (const opener of document.querySelectorAll('[data-procurement-open]')) {
      opener.setAttribute('aria-label', `${controller.labels.title}: ${count}`);
      if (controller.root.id) opener.setAttribute('aria-controls', controller.root.id);
      if (opener.id === 'cart-icon-bubble') {
        // Replace only the native visual count, never native cart state or theme events.
        for (const native of opener.querySelectorAll('.cart-count-bubble')) {
          if (!native.hasAttribute('data-procurement-bubble')) native.hidden = true;
        }
        let bubble = opener.querySelector('[data-procurement-bubble]');
        if (!bubble) {
          bubble = element('span', 'cart-count-bubble tplx-procurement__bubble');
          bubble.setAttribute('data-procurement-bubble', '');
          bubble.setAttribute('aria-hidden', 'true');
          opener.append(bubble);
        }
        bubble.textContent = String(count); bubble.hidden = count === 0;
      }
      for (const node of opener.querySelectorAll('[data-procurement-count]')) node.textContent = String(count);
    }
  }

  function initialize() {
    if (!document) return;
    for (const [root, controller] of controllers) {
      if (!root.isConnected) { controller.close(); controllers.delete(root); }
    }
    for (const root of document.querySelectorAll('[data-procurement-root]')) {
      if (!root.dataset.storageKey || controllers.has(root)) continue;
      controllers.set(root, new QuoteController(root));
    }
    for (const form of document.querySelectorAll('form[data-procurement-add]')) updateCard(form);
    for (const store of stores.values()) { refreshSnapshots(store); renderStore(store); }
    if (listenersInstalled) return;
    listenersInstalled = true;
    // Delegation supports form-associated inputs outside the form and section replacement.
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!form.matches('form[data-procurement-add]')) return;
      event.preventDefault(); event.stopImmediatePropagation();
      const controller = controllerForForm(form);
      if (!controller) return;
      try {
        const item = readCard(form, controller.currency, origin(), Date.now());
        const accepted = mutate(controller, (items) => mergeItems(items, item), 'added');
        if (accepted) form.elements.namedItem('quantity').setCustomValidity('');
        cardStatus(form, controller, accepted ? 'added' : controller.lastError);
      } catch (_) { controller.sync('invalid_quantity'); cardStatus(form, controller, 'invalid_quantity'); }
    }, true);
    for (const type of ['input', 'change']) {
      document.addEventListener(type, (event) => {
        const form = event.target.form;
        if (form?.matches('form[data-procurement-add]')) {
          event.target.setCustomValidity?.('');
          updateCard(form);
        }
      });
    }
    global.addEventListener('click', (event) => {
      const trigger = event.target.closest?.('[data-procurement-open]');
      const controller = activeController();
      if (!trigger || !controller) return;
      event.preventDefault(); event.stopImmediatePropagation();
      controller.open(trigger);
    }, true);
    global.addEventListener('storage', (event) => {
      const store = stores.get(event.key);
      // Read the latest disk value, not a potentially stale queued storage-event payload.
      if (store) { reload(store); refreshSnapshots(store); renderStore(store); }
      else if (event.key === null) {
        for (const entry of stores.values()) { reload(entry); renderStore(entry); }
      }
    });
    document.addEventListener('shopify:section:load', initialize);
  }

  const api = Object.freeze({ initialize, testing: pure });
  if (typeof module !== 'undefined' && module.exports) module.exports = pure;
  if (document) {
    global.TerplaxProcurementQuote = api;
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
    else initialize();
  }
})(typeof window === 'undefined' ? globalThis : window);
